import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExportActions } from '@evi/ui';
import { resolvePersonaId } from '@evi/shared';
import {
  fetchBookingHistory,
  bookingHistoryCsvPath,
  type BookingHistoryRow,
  type SessionStatusRollup,
} from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { downloadAuthenticatedCsv } from '@/lib/downloadAuthenticatedCsv';
import { downloadErrorMessage } from '@/lib/downloadAuthed';
import { ResponsiveTable } from '@/components/ui/ResponsiveTable';
import {
  columnsForPersona,
  type PersonaColumn,
} from '@/lib/personaColumns';
import styles from './BuildingAdminReportsPage.module.css';

interface BookingHistoryTabProps {
  /** Period chip value from the shell. Post-reports-custom-range
   *  all five chips (24h/7d/30d/90d/custom) are supported; the
   *  shell resolves them all to the same `from`/`to` ISO pair so
   *  this tab no longer branches on which chip is active. */
  period: string;
  /** Resolved range start (ISO) from the shell. */
  from: string;
  /** Resolved range end (ISO) from the shell. */
  to: string;
  /** Single location filter id (or null = all in scope). */
  locationId: string | null;
  locationIds?: readonly string[];
}

const PAGE_SIZE = 25;

// Status filter chip inventory. Includes 'all' as the no-filter
// option (null on the wire). The four rollup values match the
// server's accepted enum set (see sessionStatusRollup.ts on the
// API side).
const STATUS_CHIPS: ReadonlyArray<{ id: 'all' | SessionStatusRollup; label: string }> = [
  { id: 'all',          label: 'All' },
  { id: 'in_progress',  label: 'In progress' },
  { id: 'completed',    label: 'Completed' },
  { id: 'cancelled',    label: 'Cancelled' },
  { id: 'failed',       label: 'Failed' },
];

const STATUS_BADGE_CLASS: Record<SessionStatusRollup, string> = {
  in_progress: styles.statusInProgress!,
  completed:   styles.statusCompleted!,
  cancelled:   styles.statusCancelled!,
  failed:      styles.statusFailed!,
};

const STATUS_DISPLAY: Record<SessionStatusRollup, string> = {
  in_progress: 'In progress',
  completed:   'Completed',
  cancelled:   'Cancelled',
  failed:      'Failed',
};

// Column definitions. Tenant column gates on visibleFor:
// ['super_admin'] — building admins + tenant admins (within-tenant
// view) drop it; super_admin (spanning tenants) keeps it. All other
// columns are visible to every persona that lands on this tab.
const BOOKING_HISTORY_COLUMNS: ReadonlyArray<PersonaColumn<BookingHistoryRow>> = [
  {
    key: 'customer_name',
    header: 'Customer',
    mobile: 'primary',
    cell: (r) => r.customer_name ?? <Dim>—</Dim>,
  },
  {
    key: 'tenant_name',
    header: 'Tenant',
    visibleFor: ['super_admin'],
    cell: (r) => r.tenant_name ?? <Dim>—</Dim>,
  },
  {
    key: 'charger_identity',
    header: 'Charger',
    mobile: 'secondary',
    cell: (r) => r.charger_identity ?? <Dim>—</Dim>,
  },
  {
    key: 'status',
    header: 'Status',
    cell: (r) => (
      <span className={`${styles.statusBadge} ${STATUS_BADGE_CLASS[r.status]}`}>
        {STATUS_DISPLAY[r.status]}
      </span>
    ),
  },
  {
    key: 'started_at',
    header: 'Date & Time',
    cell: (r) => <span className={styles.dimText}>{formatDateTime(r.started_at)}</span>,
  },
  {
    key: 'amount',
    header: 'Amount',
    align: 'right',
    cell: (r) => `$${(r.amount_cents / 100).toFixed(2)}`,
  },
  {
    key: 'energy_kwh',
    header: 'kWh',
    align: 'right',
    cell: (r) => r.energy_kwh.toFixed(2),
  },
  {
    key: 'duration',
    header: 'Session Time',
    align: 'right',
    cell: (r) => formatHMS(r.duration_minutes),
  },
];

export function BookingHistoryTab({
  period, from, to, locationId, locationIds,
}: BookingHistoryTabProps): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const persona = resolvePersonaId({
    role: user?.role,
    is_sales_agent: user?.is_sales_agent ?? false,
  });

  const [statusFilter, setStatusFilter] = useState<'all' | SessionStatusRollup>('all');
  const [page, setPage] = useState(1);

  // Reset to page 1 when filters change — otherwise a 5-page result
  // becomes a 1-page result and the user is stuck on a now-empty
  // page 5. The resolved range (from/to) is keyed via period +
  // custom-range URL state at the shell layer; period change
  // already triggers a re-key here.
  useEffect(() => { setPage(1); }, [period, from, to, locationId, locationIds, statusFilter]);

  const bookingsQ = useQuery({
    // Include from/to in the key so a custom-range tweak invalidates
    // independently of period (the period stays 'custom' but the
    // actual range shifts).
    queryKey: ['building-admin-reports', 'booking-history', user?._id, from, to, locationId, locationIds, statusFilter, page],
    queryFn: () => fetchBookingHistory({
      from,
      to,
      ...(locationIds?.length ? { location_ids: locationIds } : locationId ? { location_id: locationId } : {}),
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      page,
      limit: PAGE_SIZE,
    }),

    staleTime: 30_000,
  });

  // CSV export — server caps at 10_000 rows; users who hit that
  // narrow filters and re-export. Local csvBusy state drives the
  // ExportActions loading indicator.
  // A failed export says why (29 Oct 2026), instead of failing silently.
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);
  const handleDownloadCsv = useCallback(async () => {
    if (csvBusy) return;
    setCsvBusy(true);
    setCsvError(null);
    try {
      const csvSpec = bookingHistoryCsvPath({
        from,
        to,
        ...(locationIds?.length ? { location_ids: locationIds } : locationId ? { location_id: locationId } : {}),
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      });
      const today = new Date().toISOString().slice(0, 10);
      await downloadAuthenticatedCsv({
        path: csvSpec.path,
        query: csvSpec.query,
        fallbackFilename: `booking-history-${today}.csv`,
      });
    } catch (err) {
      setCsvError(await downloadErrorMessage(err));
    } finally {
      setCsvBusy(false);
    }
  }, [csvBusy, from, to, locationId, locationIds, statusFilter]);

  const columns = useMemo(() => columnsForPersona(BOOKING_HISTORY_COLUMNS, persona), [persona]);

  // Post-reports-custom-range: no period-gating notice here. All
  // five chips resolve to a valid from/to via the shell, so the
  // tab just renders. Server enforces the 365-day cap; any range
  // error surfaces separately from a successful empty response.

  const rows = bookingsQ.data?.rows ?? [];
  const total = bookingsQ.data?.total ?? 0;
  const totalPages = bookingsQ.data?.total_pages ?? 1;

  if (bookingsQ.isError) return <div className={styles.tabBody} role="alert"><p>Could not load this report. This is a request failure, not an empty report.</p><button className={styles.pagerBtn} onClick={() => void bookingsQ.refetch()}>Retry report</button></div>;

  return (
    <div className={styles.tabBody}>
      <div className={styles.toolbar}>
        <div className={styles.statusChips} role="radiogroup" aria-label="Status filter">
          {STATUS_CHIPS.map((chip) => {
            const isActive = statusFilter === chip.id;
            return (
              <button
                key={chip.id}
                type="button"
                role="radio"
                aria-checked={isActive}
                className={`${styles.chip} ${isActive ? styles.chipActive : ''}`}
                onClick={() => setStatusFilter(chip.id)}
              >
                {chip.label}
              </button>
            );
          })}
        </div>
        <div className={styles.toolbarRight}>
          <span className={styles.toolbarMeta}>
            {total > 0 ? `${total.toLocaleString()} total` : ''}
          </span>
          <ExportActions onCsv={handleDownloadCsv} csvBusy={csvBusy} pdfHref={null} />
        </div>
      </div>
      {csvError && <p className={styles.downloadError} role="alert">{csvError}</p>}

      <div className={styles.tableCard}>
        <ResponsiveTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.session_id}
          emptyMessage={bookingsQ.isLoading ? 'Loading…' : 'No sessions in this date range for the selected locations and status. Try a longer period or different filters.'}
        />
      </div>

      {totalPages > 1 && (
        <div className={styles.pager}>
          <span className={styles.pagerMeta}>Page {page} of {totalPages}</span>
          <div className={styles.pagerButtons}>
            <button
              type="button"
              className={styles.pagerBtn}
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1}
            >Previous</button>
            <button
              type="button"
              className={styles.pagerBtn}
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
            >Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Dim({ children }: { children: ReactNode }): JSX.Element {
  return <span className={styles.dimText}>{children}</span>;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString();
}

function formatHMS(minutes: number): string {
  const totalSec = Math.max(0, Math.round((minutes ?? 0) * 60));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// periodToFromTo removed in reports-custom-range — the shell's
// resolveRange handles all five chips (24h/7d/30d/90d/custom)
// uniformly and threads the resolved from/to through
// TabRenderState. Tab body consumes the props directly.
